import { DEFAULT_LANGUAGE } from '@cibli/shared';
import { Micdrop } from '@micdrop/web';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { useLanguageContext } from '@/features/common/contexts/language.context';
import { useTrackingContext } from '@/features/common/contexts/tracking.context';
import { useUserContext } from '@/features/common/contexts/user.context';
import { INTERVIEW_STEPS } from '@/features/interview/contexts/interview-flow.context';
import { useBooth } from '@/features/settings/hooks/useBooth';

import { useRegisterContext } from '../contexts/register-context.ref';
import { useKioskOtp } from './useKioskOtp';

export type UseFlowResetReturn = {
  onReset: (step?: INTERVIEW_STEPS) => Promise<void>;
  handleBackToOnboardingWithReset: () => void;
};

export function useFlowReset(): UseFlowResetReturn {
  const { login, logout } = useUserContext();
  const { setLanguage } = useLanguageContext();
  const { resetSession } = useTrackingContext();
  const { isBooth, loading: boothLoading, booth, isBoothDefaultLocaleEnabled } = useBooth();
  const navigate = useNavigate();
  const { clearOtpState } = useKioskOtp({ login });
  const { setRegisterInitialData, setFormError } = useRegisterContext();

  const onReset = useCallback(
    async (step?: INTERVIEW_STEPS) => {
      try {
        const interviewStep = typeof step === 'string' ? step : undefined;
        await resetSession(interviewStep);
      } catch (e) {
        console.error('Failed to reset session analytics:', e);
      }

      try {
        Micdrop.conversation = [];
      } catch (e) {
        console.error('Failed to reset Micdrop conversation:', e);
      }

      try {
        // Session ends → back to the booth's default locale (not the last used one)
        await setLanguage(
          (isBoothDefaultLocaleEnabled ? booth?.defaultLocaleId : null) ?? DEFAULT_LANGUAGE,
        );
      } catch (e) {
        console.error('Failed to reset language:', e);
      }

      try {
        clearOtpState();
        setRegisterInitialData({});
        setFormError(null);
        if (!boothLoading && isBooth) {
          void logout();
        }
      } catch (e) {
        console.error('Failed to clear state on reset:', e);
      } finally {
        window.location.href = '/';
      }
    },
    [
      resetSession,
      setLanguage,
      clearOtpState,
      setRegisterInitialData,
      setFormError,
      logout,
      boothLoading,
      isBooth,
      booth?.defaultLocaleId,
      isBoothDefaultLocaleEnabled,
    ],
  );

  const handleBackToOnboardingWithReset = useCallback(() => {
    clearOtpState();
    navigate('/onboarding', { replace: true });
  }, [clearOtpState, navigate]);

  return { onReset, handleBackToOnboardingWithReset };
}
